<template>
  <div
    v-if="modelValue || value"
    :class="componentClasses"
  >
    <!-- Backdrop -->
    <div
      class="stk-drawer-backdrop"
      @click="emitClose"
    />

    <!-- Drawer -->
    <div class="stk-drawer-side">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, onBeforeUnmount, watch } from 'vue';

const OVERFLOW_HIDDEN_CLASS = 'tw-overflow-hidden';

const props = defineProps({
  modelValue: { type: Boolean, default: undefined }, // Vue 3
  value: { type: Boolean, default: undefined }, // Vue 2
});

const emit = defineEmits(['update:modelValue', 'input']);

// Determine the open state
const isOpen = computed(() => {
  // Use modelValue if defined, otherwise fallback to value (Vue 2)
  return props.modelValue !== undefined ? props.modelValue : props.value;
});

const componentClasses = computed(() => [
  'stk-drawer',
  isOpen.value ? 'is-open' : '',
]);

// Close function: emit correct event for both versions
function emitClose() {
  emit('update:modelValue', false); // Vue 3
  emit('input', false); // Vue 2
}

onBeforeUnmount(() => {
  document.documentElement.classList.remove(OVERFLOW_HIDDEN_CLASS);
});

watch(
    () => props.value,
    isOpen => {
      if (isOpen) {
        document.documentElement.classList.add(OVERFLOW_HIDDEN_CLASS);
      } else {
        document.documentElement.classList.remove(OVERFLOW_HIDDEN_CLASS);
      }
    },
    { immediate: true },
);
</script>

<style>
.stk-drawer {
  inset: 0;
  position: fixed;
  z-index: 20;

  &.is-open {
    .stk-drawer-side {
      opacity: 1;
      pointer-events: auto;
    }
  }
}

.stk-drawer-backdrop {
  animation-name: stk-fade-in;
  animation-duration: var(--stk-transition-duration);
  animation-fill-mode: both;
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
}

.stk-drawer-side {
  animation-name: stk-slide-in-from-right;
  animation-duration: var(--stk-transition-duration);
  animation-fill-mode: both;
  background-color: var(--stk-scheme-main);
  box-shadow: 0 0 1em rgb(0 0 0 / 10%);
  bottom: 0;
  right: 0;
  opacity: 0;
  overflow-y: auto;
  pointer-events: none;
  position: fixed;
  top: 0;
  width: 18rem;
}

@keyframes stk-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes stk-slide-in-from-right {
  from {
    opacity: 0;
    transform: translateX(100%);
  }

  to {
    opacity: 1;
    transform: none;
  }
}
</style>
